import { assign, isArray } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { isSafari } from 'react-device-detect';
import { Platform, useWindowDimensions, ScrollView, View } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import {
  VictoryAxis,
  VictoryChart,
  VictoryCursorContainer,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip
} from './Victory';
import Spacer from '../Spacer';
import { B } from '../Typography';
import { addDays, format } from 'date-fns';

const StyledView = styled.View`
  padding: 2px;
  align-items: center;
  min-height: 200px;
`;

const StyledTextView = styled.View`
  align-items: center;
`;

const StyledText = styled(B)`
  font-size: 12px;
  text-transform: uppercase;
`;

const StyledRowView = styled.View<{ expanded: boolean }>`
  width: 100%;
  ${p => (p.expanded ? 'flex-direction: row;' : '')}
`;

const StyledLegendContainer = styled.View`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 12px;
  shadow-color: #000;
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 3;
  z-index: 10;
`;

const StyledLegendContainerBelow = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 10px 4px 10px;
  gap: 24px;
  elevation: 3;
`;

const StyledLegendItem = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`;

const StyledLegendColor = styled.View<{ color: string }>`
  width: 16px;
  height: 3px;
  background-color: ${p => p.color};
  margin-right: 8px;
  border-radius: 1.5px;
`;

const StyledLegendText = styled(B)`
  font-size: 12px;
  color: ${Colors.darkText};
  font-family: 'WorkSans-Medium';
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/*
  data - Values for the Graph
  xAxisMaxValueLabel - Maximum x-axis domain value
  yAxisMaxValueLabel - Maximum y-axis domain value
  domainYMin - Minimum y-axis Domain Value
  rightAxisDomainYMin - Minimum Compare y-axis Value
  rightAxisYMaxValueLabel - Maximum Compare y-axis domain value
*/
export type LineChartProps = {
  title?: string;
  yAxisLeftLabel?: string;
  yAxisLeftOffset?: number;
  capitalizeYAxisLeftLabel?: boolean;
  xAxisLabel?: string;
  xAxisLabelStyle?: any;
  enableRightAxis?: boolean;
  rightAxisDomainYMin?: number;
  rightAxisYMaxValueLabel?: number;
  dataPoints: any[];
  xAxisMaxValueLabel: number;
  yAxisMaxValueLabel: number;
  expanded?: boolean;
  chartStartDate: Date;
  rightAxisDataUnit?: string;
  leftAxisDataUnit?: string;
  studyStartXPoint?: number;
  domainYMin?: number;
  roundValuesLeftAxis?: boolean;
  roundValuesRightAxis?: boolean;
  xTickFormat?: (t: number) => string;
  xGridLineColor?: string;
  yTickFormat?: (t: number) => string;
  yRange?: number[];
  yTickValues?: number[];
  yGridLineColor?: string;
  showBaseline?: boolean;
  showStudyStarted?: boolean;
  pointSize?: number;
  enableXAxisHover?: boolean;
  tooltipYPosition?: number;
  rightAxisTickFormat?: (t: number) => string;
  rightAxisLineColor?: string;
  showLegend?: boolean;
  legendLabels?: string[];
  legendPosition?: 'overlay' | 'below';
  dataPointSelectMode?: 'exact' | 'nearest';
  showYAxisTicks?: boolean;
};

function preciseMultiply(num: number, factor: number) {
  return Math.round((num * factor + Number.EPSILON) * 100) / 100;
}

export function getHoursLabel(value: number) {
  if (value) {
    const time = value.toString().split('.');
    const hours = Number(time[0]);
    const mins =
      time.length === 2
        ? Math.round(preciseMultiply(Number('.' + time[1]), 60))
        : 0;
    return `${hours ? hours + ' hr' : ''}${mins && hours ? ' ' : ''}${
      mins ? mins + ' min' : ''
    }`;
  }
  return '0 hr';
}

const LineChart: React.FC<LineChartProps> = ({
  title,
  enableRightAxis = false,
  rightAxisDomainYMin,
  rightAxisYMaxValueLabel,
  yAxisLeftLabel = '',
  yAxisLeftOffset = 0,
  capitalizeYAxisLeftLabel = true,
  xAxisLabel = '',
  xAxisLabelStyle,
  dataPoints,
  xAxisMaxValueLabel,
  yAxisMaxValueLabel,
  rightAxisDataUnit = '',
  leftAxisDataUnit = '',
  chartStartDate,
  expanded = false,
  studyStartXPoint,
  domainYMin,
  roundValuesLeftAxis = true,
  roundValuesRightAxis = true,
  xTickFormat,
  xGridLineColor,
  yTickFormat,
  yRange,
  yTickValues,
  yGridLineColor = '#D8D8DC',
  showBaseline = false,
  showStudyStarted = true,
  pointSize = 5,
  enableXAxisHover = false,
  tooltipYPosition,
  rightAxisTickFormat,
  rightAxisLineColor,
  showLegend = false,
  legendLabels = [],
  legendPosition = 'overlay',
  dataPointSelectMode = 'nearest',
  showYAxisTicks = false
}) => {
  const isYArray =
    (dataPoints?.length && isArray(dataPoints[0][0]?.y)) || false;
  const isRightYArray =
    (dataPoints?.length &&
      dataPoints[1]?.length &&
      isArray(dataPoints[1][0]?.y)) ||
    false;
  const leftAxisMaxY = yAxisMaxValueLabel || 10; // set default max Y axis to 10
  // adjust startdate and data by 1 to not plot at 0 and use it as padding
  const studyStartXPosition = studyStartXPoint
    ? studyStartXPoint + 1
    : undefined;
  const adjustedStartDate = chartStartDate
    ? addDays(
        new Date(chartStartDate),
        xAxisMaxValueLabel && xAxisMaxValueLabel >= 1 ? -1 : 0
      )
    : new Date();
  const { data, isRightAxisBoolean } = useMemo(() => {
    let isRightAxisBoolean = false;
    const dataPointResult = dataPoints.map((set, index) => {
      return set.map((point: any) => {
        const getY = () => {
          let res = 0;
          if (!isNaN(point.y) && !isArray(point.y)) {
            return point.y;
          } else if (isArray(point.y)) {
            return point.y;
          } else if (typeof point.y === 'string') {
            res = point.y === 'true' ? 1 : 0;
          } else if (typeof point.y === 'boolean') {
            res = point.y ? 1 : 0;
          }
          if (index === 1) isRightAxisBoolean = true;
          return res;
        };
        return {
          x: point.x + 1,
          y: getY()
        };
      });
    });
    return { data: dataPointResult, isRightAxisBoolean };
  }, [dataPoints]);

  const chartColors = [Colors.primary, rightAxisLineColor || '#85CADB'];
  const window = useWindowDimensions();
  const chartWidth = xAxisMaxValueLabel * 62;
  const isTablet = window.width >= 740 && window.width <= 1024;
  const isDesktop = window.width >= 1025 && window.width <= 1439;
  const isLargeDesktop = window.width >= 1440;
  const Container = expanded ? ScrollView : View;
  const containerRef = useRef<ScrollView>(null);
  const xOffsets = [
    expanded
      ? Math.round(leftAxisMaxY).toString().length < 5
        ? 49.5
        : 69.5
      : Math.round(leftAxisMaxY).toString().length < 5
        ? 39.5
        : 59.5,
    window.width *
      (Platform.OS === 'ios' && isTablet && window.height <= 800
        ? 0.48
        : Platform.OS === 'web' && isTablet && window.height <= 800
          ? 0.48
          : Platform.OS === 'web' && isTablet && window.width <= 800
            ? 0.59
            : isTablet && window.width >= 900
              ? 0.47
              : isTablet
                ? 0.59
                : isDesktop
                  ? 0.37
                  : isLargeDesktop
                    ? 0.2
                    : 0.88) -
      49.5
  ];
  const tickPadding = [-3, -18];
  const anchors = ['end', 'start'];
  const graphGuideLineColors = ['white', Colors.dataTrackerGuideLineColor];
  const graphGuideTextColors = [
    Colors.lightText,
    Colors.dataTrackerGuideTextColor
  ];
  const [maxDataValue, setMaxDataValue] = useState(0);
  const [activePointA, setActivePointA] = useState<{
    x: number;
    y: number | number[];
  } | null>();
  const [activePointB, setActivePointB] = useState<{
    x: number;
    y: number | number[];
  } | null>();
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    setActivePointA(undefined);
    setActivePointB(undefined);
    xAxisData(data);
  }, [data]);

  // Retrieve x-axis data
  const xAxisData = (data: any) => {
    data.map((dataValues: { x: number; y: number }[], index: number) => {
      if (index === 0) {
        const newArray = dataValues.map((item: { x: number }) => item.x);
        setMaxDataValue(Math.floor(Math.max(...newArray)));
      }
    });
  };

  const expandedTickValues = useMemo(() => {
    if (yTickValues && domainYMin) {
      const validTicks = yTickValues
        .filter(tick => tick >= 0 && tick <= 1)
        .map(tick => {
          return domainYMin + tick * (1 - domainYMin);
        })
        .sort((a, b) => a - b);

      const result = [...new Set(validTicks)];
      if (result.length === 0 || result[0] !== domainYMin) {
        result.unshift(domainYMin);
      }
      if (result[result.length - 1] !== 1) {
        result.push(1);
      }

      return result;
    } else if (yTickValues) {
      return yTickValues;
    }
  }, [yTickValues, domainYMin]);

  const rightAxisMaxY = useMemo(() => {
    if (data && data.length > 1) {
      const dataPoints = data[1];
      return rightAxisYMaxValueLabel
        ? rightAxisYMaxValueLabel
        : Math.max(
            ...dataPoints.map((data: any) =>
              isArray(data.y) ? Math.max(...data.y.map((y: any) => y)) : data.y
            ),
            0.5
          ) * 1.1;
    } else {
      return 0;
    }
  }, [data, rightAxisYMaxValueLabel]);

  const { leftAxisMinY, rightAxisMinY } = useMemo(() => {
    const [leftData = [], rightData = []] = data;
    const getMinY = (points: { y: number | number[] }[]) =>
      points.length
        ? Math.min(
            ...points.map(p =>
              isArray(p.y) ? Math.min(...p.y.map(y => y)) : p.y
            )
          )
        : 0;
    return {
      leftAxisMinY: Math.min(getMinY(leftData), 0),
      rightAxisMinY: Math.min(getMinY(rightData), 0)
    };
  }, [data]);

  // Y-axis data Line Graph computation
  const yDataLineGraphManipulation = useCallback(
    (data: any, index: number, isBoolean?: boolean, forChart?: boolean) => {
      return data.map(
        (item: { x: number; y: number | number[]; label?: string }) => {
          return {
            x: item.x,
            y: isBoolean
              ? (item.y as number) / 1.1
              : index === 1 && forChart
                ? isArray(item.y) && item.y.length
                  ? item.y.map(
                      y =>
                        scaleData(
                          y,
                          rightAxisDomainYMin
                            ? rightAxisDomainYMin * 100
                            : Math.min(rightAxisMinY, 0),
                          rightAxisMaxY,
                          domainYMin
                            ? domainYMin * 100
                            : Math.max(leftAxisMinY, rightAxisMinY),
                          leftAxisMaxY
                        ) / leftAxisMaxY
                    )
                  : scaleData(
                      item.y as number,
                      rightAxisDomainYMin
                        ? rightAxisDomainYMin * 100
                        : Math.min(rightAxisMinY, 0),
                      rightAxisMaxY,
                      domainYMin
                        ? domainYMin * 100
                        : Math.max(leftAxisMinY, rightAxisMinY),
                      leftAxisMaxY
                    ) / leftAxisMaxY
                : isArray(item.y) && item.y.length
                  ? item.y.map(
                      y => y / (index === 0 ? leftAxisMaxY : rightAxisMaxY)
                    )
                  : (() => {
                      if (index === 0 && yRange && yRange.length >= 2) {
                        // Use yRange for normalization when available
                        const rangeMin = yRange[0];
                        const rangeMax = yRange[1];

                        return (
                          ((item.y as number) - rangeMin) /
                          (rangeMax - rangeMin)
                        );
                      }
                      return (
                        (item.y as number) /
                        (index === 0 ? leftAxisMaxY : rightAxisMaxY)
                      );
                    })(),
            label: item.label
          };
        }
      );
    },
    [
      domainYMin,
      leftAxisMaxY,
      leftAxisMinY,
      rightAxisDomainYMin,
      rightAxisMaxY,
      rightAxisMinY,
      yRange
    ]
  );

  const { leftAxisMinScaledY, rightAxisMinScaledY } = useMemo(() => {
    const [leftData = [], rightData = []] = data.map((dataPoints, index) =>
      yDataLineGraphManipulation(dataPoints, index)
    );
    const getMinY = (points: { y: number | number[] }[]) =>
      points.length
        ? Math.min(
            ...points.map(p =>
              isArray(p.y) ? Math.min(...p.y.map(y => y)) : p.y
            )
          )
        : 0;
    return {
      leftAxisMinScaledY: Math.min(getMinY(leftData), 0),
      rightAxisMinScaledY: Math.min(getMinY(rightData), 0)
    };
  }, [data, yDataLineGraphManipulation]);

  function scaleData(
    x: number,
    oldMin: number,
    oldMax: number,
    newMin: number,
    newMax: number
  ) {
    return ((x - oldMin) / (oldMax - oldMin)) * (newMax - newMin) + newMin;
  }

  const getChartElementStyles = (index: number) => {
    const xAxisTickStyle = {
      ticks: { padding: -2 },
      tickLabels: {
        fill: (tick: any) =>
          tick.index < maxDataValue
            ? Colors.darkText
            : Colors.dataTrackerTickDisabled,
        fontSize: '12px',
        fontFamily: 'WorkSans-Medium',
        lineHeight: '22px',
        letterSpacing: '-0.49px'
      }
    };

    const yAxisTickStyle = {
      ticks: { padding: tickPadding[index] },
      tickLabels: {
        fill: Colors.darkText,
        fontSize: '12px',
        fontFamily: 'WorkSans-Medium',
        lineHeight: '83.85px',
        letterSpacing: '-0.49px',
        textAnchor: anchors[index]
      }
    };

    const xAxisStyle = assign({}, xAxisTickStyle, {
      grid: {
        stroke: 'transparent'
      },
      axis: { stroke: '#D8D8DC', strokeWidth: '1.5px' },

      axisLabel: xAxisLabelStyle
        ? xAxisLabelStyle
        : {
            fill: Colors.darkText,
            fontSize: '10px',
            fontFamily: 'WorkSans-SemiBold',
            lineHeight: '83.85px',
            letterSpacing: '-0.49px'
          },
      ticks: { padding: expanded ? 0 : -6 },
      tickLabels: {
        fill: Colors.darkText,
        fontSize: '12px',
        fontFamily: 'WorkSans-Medium',
        lineHeight: '14px'
      }
    });

    const xAxisDummyLineStyle = {
      axis: { stroke: 'rgba(239, 242, 243, 0.3)' },
      ticks: { padding: -10.5 },
      tickLabels: {
        fill: Colors.darkText,
        fontSize: '27px',
        fontFamily: 'WorkSans-SemiBold'
      },
      grid: { stroke: 'transparent' }
    };

    const yAxisStyle = assign({}, yAxisTickStyle, {
      grid: { stroke: yGridLineColor || Colors.lightText, strokeWidth: 0 },
      axis: { stroke: '#D8D8DC', strokeWidth: '1.5px' },
      ticks: {
        padding: expanded ? (showYAxisTicks ? 0 : 8) : 0,
        ...(showYAxisTicks && expanded
          ? { stroke: '#D8D8DC', strokeWidth: '1px', size: 4 }
          : { stroke: 'transparent', strokeWidth: 0 })
      },
      tickLabels: {
        fill: Colors.darkText,
        fontSize: '12px',
        fontFamily: 'WorkSans-Medium',
        lineHeight: '14px',
        padding: 8
      }
    });

    const labelStyle = {
      fill: index === -1 ? Colors.lightText : graphGuideTextColors[index],
      fontSize: '10px',
      fontFamily: 'WorkSans-SemiBold',
      letterSpacing: '-0.49px'
    };

    const guideLineStyle = {
      data: {
        stroke: graphGuideLineColors[index],
        strokeWidth: 1,
        strokeDasharray: '3, 1'
      }
    };

    const lineStyle = {
      data: {
        stroke: chartColors[index],
        strokeWidth: '1.5px'
      }
    };

    const dummyLine = {
      data: {
        stroke: '#D8D8DC',
        strokeWidth: '1px'
      }
    };

    const startLine = {
      data: {
        stroke: xGridLineColor || 'red',
        strokeWidth: 1.5
      }
    };

    const scatterStyle = {
      data: {
        fill: 'white',
        stroke: chartColors[index],
        strokeWidth: 1.5
      }
    };

    return {
      labels: labelStyle,
      xAxis: xAxisStyle,
      xAxisDummyLine: xAxisDummyLineStyle,
      yAxis: yAxisStyle,
      guideLine: guideLineStyle,
      line: lineStyle,
      dummyLine: dummyLine,
      startLine: startLine,
      scatter: scatterStyle
    };
  };

  const handleCursorChange = (e: any) => {
    if (e !== null && (expanded || enableXAxisHover)) {
      if (dataPointSelectMode === 'nearest') {
        const findNearestPoint = (dataset: any[], targetX: number) => {
          if (!dataset || dataset.length === 0) return null;

          return dataset.reduce((nearest, current) => {
            const nearestDistance = Math.abs(nearest.x - targetX);
            const currentDistance = Math.abs(current.x - targetX);
            return currentDistance < nearestDistance ? current : nearest;
          });
        };

        const dataA =
          typeof data[0] === 'undefined' ? null : findNearestPoint(data[0], e);
        const dataB =
          typeof data[1] === 'undefined' || data[1].length === 0
            ? null
            : findNearestPoint(data[1], e);

        if (dataA) {
          setActivePointA(dataA);
          if (data[1] && data[1].length > 0) {
            setActivePointB(findNearestPoint(data[1], e));
          }
        } else if (dataB) {
          setActivePointB(dataB);
          if (data[0] && data[0].length > 0) {
            setActivePointA(findNearestPoint(data[0], e));
          }
        }
      } else {
        const dataA =
          typeof data[0] === 'undefined'
            ? null
            : data[0].find((d: any) => d.x === Math.round(e));
        const dataB =
          typeof data[1] === 'undefined'
            ? null
            : data[1].find((d: any) => d.x === Math.round(e));
        if (dataA) {
          setActivePointA(dataA);
          if (data[1].length > 0) {
            setActivePointB(data[1].find((e: any) => e.x === dataA.x));
          }
        } else if (dataB) {
          setActivePointB(dataB);
          if (data[0].length > 0) {
            setActivePointA(data[0].find((e: any) => e.x === dataB.x));
          }
        }
      }
    }
  };

  const denormalized = useCallback(
    (min: number, max: number, roundedValues: boolean) => {
      if (roundedValues) {
        const roundedMax = Math.round(max);
        if (roundedMax === 2) return [0, 0.5, 1];
        if (roundedMax <= 1) return [0, 1];
      }
      const normalized = [0, 0.33, 0.66, 1];
      const denormalized = normalized.map(n => min + (1 - min) * n);
      return denormalized;
    },
    []
  );

  const mapTouchToTickX = (
    locationX: number,
    chartWidth: number,
    xAxisMaxValueLabel: number
  ) => {
    const stepWidth = chartWidth / xAxisMaxValueLabel;
    return Math.round(locationX / stepWidth);
  };

  const legendData = useMemo(() => {
    if (!showLegend || !expanded) return [];

    const legendItems = [];

    if (dataPoints.length > 0 && dataPoints[0].length > 0) {
      legendItems.push({
        color: chartColors[0],
        label: legendLabels[0] || 'Primary Data'
      });
    }

    if (enableRightAxis && dataPoints.length > 1 && dataPoints[1].length > 0) {
      legendItems.push({
        color: chartColors[1],
        label: legendLabels[1] || 'Comparison Data'
      });
    }

    return legendItems;
  }, [
    showLegend,
    expanded,
    dataPoints,
    chartColors,
    legendLabels,
    enableRightAxis
  ]);

  const splitArrData = (chartData: { y: number[]; x: number }[]) => {
    return [
      [...chartData.map(d => ({ y: d.y[0], x: d.x }))],
      [...chartData.map(d => ({ y: d.y[1], x: d.x }))]
    ];
  };

  return (
    <StyledView
      onLayout={event => {
        const { width } = event.nativeEvent.layout;
        setContainerWidth(width - (expanded ? 100 : 0));
      }}
    >
      {title ? (
        <StyledTextView>
          <StyledText color='dark'>{title}</StyledText>
        </StyledTextView>
      ) : (
        <Spacer size={2} />
      )}
      <StyledRowView expanded={expanded}>
        {expanded && (
          <View>
            <VictoryChart
              padding={{
                top: 12,
                bottom:
                  containerWidth < chartWidth && Platform.OS === 'web'
                    ? isSafari
                      ? 44
                      : 49.5
                    : Platform.OS === 'web' && enableRightAxis
                      ? isSafari
                        ? 44
                        : 49.5
                      : 36,
                left: 0,
                right: 0
              }}
              width={
                Math.round(leftAxisMaxY).toString().length < 5
                  ? 50 + yAxisLeftOffset
                  : 70 + yAxisLeftOffset
              }
              height={
                expanded
                  ? showLegend && legendPosition === 'below'
                    ? window.height - 170
                    : window.height - 210
                  : 295
              }
              domain={{
                y: [domainYMin ? domainYMin : leftAxisMinScaledY, 1]
              }}
              domainPadding={{ y: expanded ? [12, 36] : 0 }}
            >
              <VictoryAxis
                crossAxis={false}
                axisLabelComponent={<VictoryLabel dy={-yAxisLeftOffset} />}
                label={
                  capitalizeYAxisLeftLabel
                    ? yAxisLeftLabel.toUpperCase()
                    : yAxisLeftLabel
                }
                dependentAxis
                offsetX={xOffsets[0] + yAxisLeftOffset}
                style={getChartElementStyles(0).yAxis}
                tickValues={
                  yTickValues
                    ? expandedTickValues
                    : domainYMin
                      ? [domainYMin, 1]
                      : leftAxisDataUnit === 'steps'
                        ? [0, 0.25, 0.5, 0.75, 1]
                        : denormalized(
                            leftAxisMinScaledY,
                            leftAxisMaxY,
                            roundValuesLeftAxis
                          )
                }
                tickFormat={
                  data[0].length === 0
                    ? () => ''
                    : (tickValue: number) =>
                        roundValuesLeftAxis
                          ? Math.round(tickValue * leftAxisMaxY)
                          : Number((tickValue * leftAxisMaxY).toFixed(1))
                }
              />
            </VictoryChart>
          </View>
        )}
        <Container
          ref={containerRef}
          onContentSizeChange={() =>
            containerRef?.current?.scrollToEnd({ animated: true })
          }
          horizontal
          scrollEnabled={expanded}
          bounces={false}
          overScrollMode='never'
        >
          <VictoryChart
            containerComponent={
              <VictoryCursorContainer
                cursorDimension='x'
                onCursorChange={handleCursorChange}
                cursorComponent={<View />}
              />
            }
            padding={{
              top: 12,
              bottom: expanded ? 36 : 60,
              left: expanded
                ? -4
                : Math.round(leftAxisMaxY).toString().length < 5
                  ? 40 + yAxisLeftOffset
                  : 60 + yAxisLeftOffset,
              right: expanded
                ? enableRightAxis
                  ? -4
                  : 30
                : Platform.OS === 'web'
                  ? 39.5
                  : 88
            }}
            width={
              expanded
                ? containerWidth > chartWidth
                  ? containerWidth + 6
                  : chartWidth
                : Platform.OS === 'web'
                  ? containerWidth
                  : window.width
            }
            height={
              expanded
                ? showLegend && legendPosition === 'below'
                  ? window.height - 170
                  : window.height - 210
                : 295
            }
            domain={{
              y: [domainYMin ? domainYMin : leftAxisMinScaledY, 1]
            }}
            domainPadding={{
              y: expanded ? [12, 36] : 12,
              x: expanded ? 18 : 0
            }}
          >
            {/* Y-Axis */}
            {!expanded && (
              <VictoryAxis
                crossAxis={false}
                axisLabelComponent={<VictoryLabel dy={-yAxisLeftOffset} />}
                label={
                  capitalizeYAxisLeftLabel
                    ? yAxisLeftLabel.toUpperCase()
                    : yAxisLeftLabel
                }
                dependentAxis
                width={Math.round(leftAxisMaxY).toString().length < 5 ? 50 : 70}
                offsetX={xOffsets[0] + yAxisLeftOffset}
                style={getChartElementStyles(0).yAxis}
                tickValues={
                  yTickValues
                    ? yTickValues
                    : domainYMin
                      ? [domainYMin, 1]
                      : leftAxisDataUnit === 'steps'
                        ? [0, 0.25, 0.5, 0.75, 1]
                        : denormalized(
                            leftAxisMinScaledY,
                            leftAxisMaxY,
                            roundValuesLeftAxis
                          )
                }
                tickFormat={
                  data[0].length === 0
                    ? () => ''
                    : yTickFormat
                      ? (t: any) => yTickFormat(Number(t))
                      : (tickValue: number) =>
                          roundValuesLeftAxis
                            ? Math.round(tickValue * leftAxisMaxY)
                            : Number((tickValue * leftAxisMaxY).toFixed(1))
                }
              />
            )}

            {/* X-Axis */}
            <VictoryAxis
              key={'x-axis'}
              label={xAxisLabel ? xAxisLabel : undefined}
              padding={{ top: 20, bottom: 60 }}
              offsetY={expanded ? 35.2 : 59.2}
              tickFormat={
                xTickFormat
                  ? (t: any) => xTickFormat(Number(t))
                  : (t: any) =>
                      xAxisMaxValueLabel
                        ? t === xAxisMaxValueLabel + 1 && expanded
                          ? ''
                          : format(addDays(adjustedStartDate, t), 'M/d')
                        : ''
              }
              style={getChartElementStyles(0).xAxis}
              domain={{
                x:
                  xAxisMaxValueLabel === 1
                    ? [1]
                    : xAxisMaxValueLabel === 2
                      ? [0, 1, 2]
                      : [
                          0.75,
                          expanded ? xAxisMaxValueLabel + 1 : xAxisMaxValueLabel
                        ]
              }}
              tickCount={xAxisMaxValueLabel}
            />
            {/* Graph */}
            {(activePointA || activePointB) && (
              <VictoryLine
                style={getChartElementStyles(0).dummyLine}
                data={yDataLineGraphManipulation(
                  [
                    {
                      x: activePointA?.x || activePointB?.x,
                      y: leftAxisMaxY + 1
                    },
                    {
                      x: activePointA?.x || activePointB?.x,
                      y:
                        yRange && yRange.length >= 2
                          ? yRange[0]
                          : leftAxisMinScaledY
                    }
                  ],
                  0
                )}
              />
            )}
            {typeof studyStartXPosition !== 'undefined' && (
              <VictoryLine
                style={getChartElementStyles(0).startLine}
                data={yDataLineGraphManipulation(
                  [
                    {
                      x: studyStartXPosition,
                      y:
                        yRange && yRange.length >= 2
                          ? yRange[0]
                          : leftAxisMinScaledY
                    },
                    {
                      x: studyStartXPosition,
                      y:
                        yRange && yRange.length >= 2
                          ? yRange[1]
                          : leftAxisMinScaledY
                    }
                  ],
                  0
                )}
              />
            )}
            {showBaseline && (
              <VictoryLine
                style={{
                  data: {
                    stroke: '#D8D8DC',
                    strokeWidth: 1,
                    strokeDasharray: '3, 3'
                  }
                }}
                data={[
                  {
                    x: 0,
                    y:
                      yRange && yRange.length >= 2
                        ? (-1 * yRange[0]) / (yRange[1] - yRange[0])
                        : 0
                  },
                  {
                    x: xAxisMaxValueLabel,
                    y:
                      yRange && yRange.length >= 2
                        ? (-1 * yRange[0]) / (yRange[1] - yRange[0])
                        : 0
                  }
                ]}
              />
            )}
            {studyStartXPosition &&
              showStudyStarted &&
              activePointA?.x !== studyStartXPosition &&
              activePointB?.x !== studyStartXPosition && (
                <VictoryScatter
                  style={{
                    data: {
                      fill: 'transparent',
                      stroke: 'transparent',
                      strokeWidth: 0
                    }
                  }}
                  labels={() => 'Study Started'}
                  labelComponent={
                    <VictoryTooltip
                      constrainToVisibleArea
                      renderInPortal={false}
                      active
                      flyoutPadding={4}
                      cornerRadius={4}
                      pointerLength={4}
                      flyoutHeight={38}
                      pointerWidth={0}
                      flyoutStyle={{
                        stroke: 'none',
                        strokeWidth: 10,
                        fill: 'white'
                      }}
                      style={{
                        fill: 'red',
                        fontSize: '14px',
                        fontFamily: 'WorkSans-Regular'
                      }}
                    />
                  }
                  size={pointSize}
                  samples={1}
                  data={yDataLineGraphManipulation(
                    [
                      {
                        x: studyStartXPosition,
                        y: leftAxisMaxY
                      }
                    ],
                    0
                  )}
                />
              )}
            {data.map((data: any, index: number) =>
              index === 0 ? (
                isYArray ? (
                  splitArrData(data).map(d => (
                    <VictoryLine
                      key={index}
                      style={getChartElementStyles(index).line}
                      interpolation='monotoneX'
                      data={yDataLineGraphManipulation(d, index)}
                    />
                  ))
                ) : (
                  <VictoryLine
                    key={index}
                    style={getChartElementStyles(index).line}
                    interpolation='monotoneX'
                    data={yDataLineGraphManipulation(data, index)}
                  />
                )
              ) : (
                enableRightAxis &&
                (isRightYArray ? (
                  splitArrData(data).map(d => (
                    <VictoryLine
                      key={index}
                      style={getChartElementStyles(index).line}
                      interpolation='monotoneX'
                      data={yDataLineGraphManipulation(
                        d,
                        index,
                        isRightAxisBoolean,
                        true
                      )}
                    />
                  ))
                ) : (
                  <VictoryLine
                    key={index}
                    style={getChartElementStyles(index).line}
                    interpolation='monotoneX'
                    data={yDataLineGraphManipulation(
                      data,
                      index,
                      isRightAxisBoolean,
                      true
                    )}
                  />
                ))
              )
            )}

            {data.map((value: any, index: number) =>
              index === 0 ? (
                isYArray ? (
                  splitArrData(value).map(d => (
                    <VictoryScatter
                      key={index}
                      style={getChartElementStyles(index).scatter}
                      size={pointSize}
                      samples={xAxisMaxValueLabel}
                      data={yDataLineGraphManipulation(d, index)}
                    />
                  ))
                ) : (
                  <VictoryScatter
                    key={index}
                    style={getChartElementStyles(index).scatter}
                    size={pointSize}
                    samples={xAxisMaxValueLabel}
                    data={yDataLineGraphManipulation(value, index)}
                  />
                )
              ) : (
                enableRightAxis &&
                (isRightYArray ? (
                  splitArrData(value).map(d => (
                    <VictoryScatter
                      key={index}
                      style={getChartElementStyles(index).scatter}
                      size={pointSize}
                      samples={xAxisMaxValueLabel}
                      data={yDataLineGraphManipulation(
                        d,
                        index,
                        isRightAxisBoolean,
                        true
                      )}
                    />
                  ))
                ) : (
                  <VictoryScatter
                    key={index}
                    style={getChartElementStyles(index).scatter}
                    size={pointSize}
                    samples={xAxisMaxValueLabel}
                    data={yDataLineGraphManipulation(
                      value,
                      index,
                      isRightAxisBoolean,
                      true
                    )}
                  />
                ))
              )
            )}

            {activePointA ? (
              isArray(activePointA.y) ? (
                activePointA.y.map((y, index) => (
                  <VictoryScatter
                    key={'activePointA' + index}
                    style={{
                      data: {
                        fill: chartColors[0],
                        stroke: chartColors[0],
                        strokeWidth: 1.5
                      }
                    }}
                    size={5}
                    samples={1}
                    data={yDataLineGraphManipulation(
                      [{ y, x: activePointA.x }],
                      0
                    )}
                  />
                ))
              ) : (
                <VictoryScatter
                  style={{
                    data: {
                      fill: chartColors[0],
                      stroke: chartColors[0],
                      strokeWidth: 1.5
                    }
                  }}
                  size={5}
                  samples={1}
                  data={yDataLineGraphManipulation([activePointA], 0)}
                />
              )
            ) : undefined}

            {activePointB &&
              (isArray(activePointB.y) ? (
                activePointB.y.map((y, index) => (
                  <VictoryScatter
                    key={'activePointB' + index}
                    style={{
                      data: {
                        fill: chartColors[1],
                        stroke: chartColors[1],
                        strokeWidth: 1.5
                      }
                    }}
                    size={5}
                    samples={1}
                    data={yDataLineGraphManipulation(
                      [{ y, x: activePointB.x }],
                      1,
                      isRightAxisBoolean,
                      true
                    )}
                  />
                ))
              ) : (
                <VictoryScatter
                  style={{
                    data: {
                      fill: chartColors[1],
                      stroke: chartColors[1],
                      strokeWidth: 1.5
                    }
                  }}
                  size={5}
                  samples={1}
                  data={yDataLineGraphManipulation(
                    [activePointB],
                    1,
                    isRightAxisBoolean,
                    true
                  )}
                />
              ))}
            {(activePointA || activePointB) && (
              <VictoryScatter
                style={{
                  data: {
                    fill: 'transparent',
                    stroke: 'transparent',
                    strokeWidth: 0
                  }
                }}
                labels={() => {
                  const label: string[] = [];
                  if (activePointA) {
                    if (leftAxisDataUnit === 'h') {
                      label.push(getHoursLabel(activePointA.y as number));
                    } else {
                      label.push(
                        isArray(activePointA.y)
                          ? `${activePointA.y[0]}` +
                              '/' +
                              `${activePointA.y[1]}` +
                              leftAxisDataUnit
                          : activePointA.y.toLocaleString(undefined, {
                              maximumFractionDigits: 2
                            }) + leftAxisDataUnit
                      );
                    }
                  }
                  if (activePointB) {
                    if (rightAxisDataUnit === 'h') {
                      label.push(getHoursLabel(activePointB.y as number));
                    } else {
                      label.push(
                        isArray(activePointB.y)
                          ? `${activePointB.y[0]}` +
                              '/' +
                              `${activePointB.y[1]}${rightAxisDataUnit}`
                          : activePointB.y.toLocaleString(undefined, {
                              maximumFractionDigits: 2
                            }) + rightAxisDataUnit
                      );
                    }
                  }
                  return label;
                }}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea
                    renderInPortal={false}
                    active
                    flyoutPadding={12}
                    cornerRadius={4}
                    pointerLength={8}
                    flyoutHeight={38}
                    pointerWidth={0}
                    flyoutStyle={{
                      stroke: 'none',
                      strokeWidth: 10,
                      fill: 'white'
                    }}
                    style={[
                      {
                        fill: activePointA ? chartColors[0] : chartColors[1],
                        fontSize: '14px',
                        fontFamily: 'WorkSans-Regular'
                      },
                      {
                        fill: chartColors[1],
                        fontSize: '14px',
                        fontFamily: 'WorkSans-Regular'
                      }
                    ]}
                  />
                }
                size={5}
                samples={1}
                data={
                  activePointA || activePointB
                    ? yDataLineGraphManipulation(
                        [
                          {
                            x: activePointA
                              ? activePointA.x
                              : activePointB
                                ? activePointB.x
                                : 0,
                            y:
                              tooltipYPosition !== undefined
                                ? tooltipYPosition
                                : leftAxisMaxY
                          }
                        ],
                        0
                      )
                    : []
                }
              />
            )}
          </VictoryChart>
          {!expanded && <Spacer size={4} />}
        </Container>
        {expanded && enableRightAxis && (
          <View>
            <VictoryChart
              padding={{
                top: 12,
                bottom:
                  containerWidth < chartWidth && Platform.OS === 'web'
                    ? isSafari
                      ? 44
                      : 49.5
                    : Platform.OS === 'web'
                      ? isSafari
                        ? 44
                        : 49.5
                      : 36,
                left: 0,
                right: 0
              }}
              width={Math.round(rightAxisMaxY).toString().length < 5 ? 50 : 80}
              height={
                expanded
                  ? showLegend && legendPosition === 'below'
                    ? window.height - 170
                    : window.height - 210
                  : 295
              }
              domain={{
                y: [
                  rightAxisDomainYMin
                    ? rightAxisDomainYMin
                    : rightAxisMinScaledY,
                  1
                ]
              }}
              domainPadding={{ y: expanded ? [12, 36] : 0 }}
            >
              <VictoryAxis
                orientation='right'
                crossAxis={false}
                dependentAxis
                offsetX={
                  Math.round(rightAxisMaxY).toString().length < 5 ? 49.5 : 79.5
                }
                style={getChartElementStyles(1).yAxis}
                tickValues={
                  isRightAxisBoolean
                    ? [0, 0.9, 1]
                    : rightAxisDomainYMin
                      ? [rightAxisDomainYMin, 1]
                      : rightAxisDataUnit === 'steps'
                        ? [0, 0.25, 0.5, 0.75, 1]
                        : denormalized(
                            rightAxisMinScaledY,
                            rightAxisMaxY,
                            roundValuesRightAxis
                          )
                }
                tickFormat={
                  rightAxisTickFormat
                    ? (t: any) => rightAxisTickFormat(Number(t))
                    : isRightAxisBoolean
                      ? (tickValue: number) =>
                          tickValue === 0.9 ? '1' : tickValue === 0 ? '0' : ''
                      : data[1].length === 0
                        ? () => ''
                        : (tickValue: number) =>
                            roundValuesRightAxis
                              ? rightAxisDataUnit === 'steps'
                                ? Math.round(
                                    (tickValue * rightAxisMaxY) / 100
                                  ) * 100
                                : Math.round(tickValue * rightAxisMaxY)
                              : Number((tickValue * rightAxisMaxY).toFixed(1))
                }
              />
            </VictoryChart>
          </View>
        )}
      </StyledRowView>
      {showLegend &&
        expanded &&
        legendData.length > 0 &&
        legendPosition === 'overlay' && (
          <StyledLegendContainer>
            {legendData.map((item, index) => (
              <StyledLegendItem key={index}>
                <StyledLegendColor color={item.color} />
                <StyledLegendText>{item.label}</StyledLegendText>
              </StyledLegendItem>
            ))}
          </StyledLegendContainer>
        )}
      {showLegend &&
        expanded &&
        legendData.length > 0 &&
        legendPosition === 'below' && (
          <StyledLegendContainerBelow>
            {legendData.map((item, index) => (
              <StyledLegendItem key={index}>
                <StyledLegendColor color={item.color} />
                <StyledLegendText>{item.label}</StyledLegendText>
              </StyledLegendItem>
            ))}
          </StyledLegendContainerBelow>
        )}
    </StyledView>
  );
};

export default LineChart;
